import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import DateTimePicker from '@/components/ui/DateTimePicker';
import {
  Filter,
  Search,
  X,
  Calendar,
  Building2,
  User,
  AlertCircle,
  SlidersHorizontal,
  RotateCcw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BookingStatus } from '@/types/activities';

export interface FilterCriteria {
  search?: string;
  status?: BookingStatus[];
  organizationIds?: string[];
  priorityLevels?: ('urgent' | 'high' | 'medium' | 'low')[];
  dateRange?: {
    start?: Date;
    end?: Date;
  };
  requiresAttention?: boolean;
  vendorIds?: string[];
  amountRange?: {
    min?: number;
    max?: number;
  };
  participantRange?: {
    min?: number;
    max?: number;
  };
}

interface AdvancedFiltersProps {
  criteria: FilterCriteria;
  onCriteriaChange: (criteria: FilterCriteria) => void;
  organizations?: Array<{ id: string; name: string }>;
  vendors?: Array<{ id: string; name: string }>;
  className?: string;
  showSearch?: boolean;
  showQuickFilters?: boolean;
}

const statusOptions: { value: BookingStatus; label: string; color: string }[] = [
  { value: BookingStatus.PendingVendorContact, label: 'Pending Vendor Contact', color: 'bg-blue-100 text-blue-800' },
  { value: BookingStatus.VendorNegotiating, label: 'Vendor Negotiating', color: 'bg-yellow-100 text-yellow-800' },
  { value: BookingStatus.PendingOrganizationApproval, label: 'Pending Organization Approval', color: 'bg-purple-100 text-purple-800' },
  { value: BookingStatus.ApprovedAwaitingVendorBooking, label: 'Awaiting Vendor Booking', color: 'bg-orange-100 text-orange-800' },
  { value: BookingStatus.Confirmed, label: 'Confirmed', color: 'bg-green-100 text-green-800' },
  { value: BookingStatus.Cancelled, label: 'Cancelled', color: 'bg-red-100 text-red-800' },
  { value: BookingStatus.NoVendorsAvailable, label: 'No Vendors Available', color: 'bg-gray-100 text-gray-800' }
];

const priorityOptions = [
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' }
];

export default function AdvancedFilters({
  criteria,
  onCriteriaChange,
  organizations = [],
  vendors = [],
  className,
  showSearch = true,
  showQuickFilters = true
}: AdvancedFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localCriteria, setLocalCriteria] = useState<FilterCriteria>(criteria);

  // Sync local state with props
  useEffect(() => {
    setLocalCriteria(criteria);
  }, [criteria]);

  const applyFilters = () => {
    onCriteriaChange(localCriteria);
    setIsOpen(false);
  };

  const resetFilters = () => {
    const resetCriteria: FilterCriteria = {};
    setLocalCriteria(resetCriteria);
    onCriteriaChange(resetCriteria);
  };

  const updateCriteria = (updates: Partial<FilterCriteria>) => {
    setLocalCriteria(prev => ({ ...prev, ...updates }));
  };

  const removeFilter = (key: keyof FilterCriteria, value?: any) => {
    const updated = { ...localCriteria };

    if (key === 'status' && value) {
      updated.status = updated.status?.filter(s => s !== value) || [];
      if (updated.status.length === 0) delete updated.status;
    } else if (key === 'organizationIds' && value) {
      updated.organizationIds = updated.organizationIds?.filter(id => id !== value) || [];
      if (updated.organizationIds.length === 0) delete updated.organizationIds;
    } else if (key === 'priorityLevels' && value) {
      updated.priorityLevels = updated.priorityLevels?.filter(p => p !== value) || [];
      if (updated.priorityLevels.length === 0) delete updated.priorityLevels;
    } else {
      delete updated[key];
    }

    setLocalCriteria(updated);
    onCriteriaChange(updated);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (criteria.search) count++;
    if (criteria.status?.length) count += criteria.status.length;
    if (criteria.organizationIds?.length) count += criteria.organizationIds.length;
    if (criteria.priorityLevels?.length) count += criteria.priorityLevels.length;
    if (criteria.dateRange?.start || criteria.dateRange?.end) count++;
    if (criteria.requiresAttention) count++;
    if (criteria.vendorIds?.length) count += criteria.vendorIds.length;
    if (criteria.amountRange?.min || criteria.amountRange?.max) count++;
    if (criteria.participantRange?.min || criteria.participantRange?.max) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search Bar */}
      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search bookings, organizations, vendors..."
            value={criteria.search || ''}
            onChange={(e) => onCriteriaChange({ ...criteria, search: e.target.value })}
            className="pl-10"
          />
        </div>
      )}

      {/* Quick Filters & Advanced Filter Button */}
      <div className="flex items-center justify-between gap-4">
        {showQuickFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={criteria.requiresAttention ? "default" : "outline"}
              size="sm"
              onClick={() => onCriteriaChange({
                ...criteria,
                requiresAttention: !criteria.requiresAttention
              })}
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Requires Attention
            </Button>

            {statusOptions.slice(0, 4).map((status) => (
              <Button
                key={status.value}
                variant={criteria.status?.includes(status.value) ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const currentStatus = criteria.status || [];
                  const newStatus = currentStatus.includes(status.value)
                    ? currentStatus.filter(s => s !== status.value)
                    : [...currentStatus, status.value];

                  onCriteriaChange({
                    ...criteria,
                    status: newStatus.length > 0 ? newStatus : undefined
                  });
                }}
              >
                {status.label}
              </Button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Active Filter Count */}
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount} active filter{activeFilterCount === 1 ? '' : 's'}
            </Badge>
          )}

          {/* Advanced Filters */}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Advanced Filters
                {activeFilterCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-4 w-4 p-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-96 p-0" align="end">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Advanced Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="text-muted-foreground"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </div>

              <ScrollArea className="max-h-96">
                <div className="p-4 space-y-6">
                  {/* Status Filters */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Status</Label>
                    <div className="space-y-2">
                      {statusOptions.map((status) => (
                        <div key={status.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`status-${status.value}`}
                            checked={localCriteria.status?.includes(status.value) || false}
                            onCheckedChange={(checked) => {
                              const currentStatus = localCriteria.status || [];
                              const newStatus = checked
                                ? [...currentStatus, status.value]
                                : currentStatus.filter(s => s !== status.value);

                              updateCriteria({
                                status: newStatus.length > 0 ? newStatus : undefined
                              });
                            }}
                          />
                          <Label
                            htmlFor={`status-${status.value}`}
                            className="text-sm cursor-pointer"
                          >
                            <Badge variant="outline" className={cn("text-xs", status.color)}>
                              {status.label}
                            </Badge>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Priority Filters */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Priority</Label>
                    <div className="space-y-2">
                      {priorityOptions.map((priority) => (
                        <div key={priority.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`priority-${priority.value}`}
                            checked={localCriteria.priorityLevels?.includes(priority.value as any) || false}
                            onCheckedChange={(checked) => {
                              const currentPriority = localCriteria.priorityLevels || [];
                              const newPriority = checked
                                ? [...currentPriority, priority.value as any]
                                : currentPriority.filter(p => p !== priority.value);

                              updateCriteria({
                                priorityLevels: newPriority.length > 0 ? newPriority : undefined
                              });
                            }}
                          />
                          <Label
                            htmlFor={`priority-${priority.value}`}
                            className="text-sm cursor-pointer"
                          >
                            <Badge variant="outline" className={cn("text-xs", priority.color)}>
                              {priority.label}
                            </Badge>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Date Range */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Date Range</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <DateTimePicker
                        label="From"
                        value={localCriteria.dateRange?.start}
                        onChange={(date) => updateCriteria({
                          dateRange: {
                            ...localCriteria.dateRange,
                            start: date
                          }
                        })}
                        showTime={false}
                      />
                      <DateTimePicker
                        label="To"
                        value={localCriteria.dateRange?.end}
                        onChange={(date) => updateCriteria({
                          dateRange: {
                            ...localCriteria.dateRange,
                            end: date
                          }
                        })}
                        showTime={false}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Organizations */}
                  {organizations.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Organizations</Label>
                      <ScrollArea className="max-h-32">
                        <div className="space-y-2">
                          {organizations.map((org) => (
                            <div key={org.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`org-${org.id}`}
                                checked={localCriteria.organizationIds?.includes(org.id) || false}
                                onCheckedChange={(checked) => {
                                  const currentOrgs = localCriteria.organizationIds || [];
                                  const newOrgs = checked
                                    ? [...currentOrgs, org.id]
                                    : currentOrgs.filter(id => id !== org.id);

                                  updateCriteria({
                                    organizationIds: newOrgs.length > 0 ? newOrgs : undefined
                                  });
                                }}
                              />
                              <Label
                                htmlFor={`org-${org.id}`}
                                className="text-sm cursor-pointer"
                              >
                                {org.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}

                  <Separator />

                  {/* Amount Range */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Budget Range (AED)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="amount-min" className="text-xs">Minimum</Label>
                        <Input
                          id="amount-min"
                          type="number"
                          placeholder="0"
                          value={localCriteria.amountRange?.min || ''}
                          onChange={(e) => updateCriteria({
                            amountRange: {
                              ...localCriteria.amountRange,
                              min: e.target.value ? Number(e.target.value) : undefined
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="amount-max" className="text-xs">Maximum</Label>
                        <Input
                          id="amount-max"
                          type="number"
                          placeholder="100000"
                          value={localCriteria.amountRange?.max || ''}
                          onChange={(e) => updateCriteria({
                            amountRange: {
                              ...localCriteria.amountRange,
                              max: e.target.value ? Number(e.target.value) : undefined
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Participant Range */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Participant Count</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="participant-min" className="text-xs">Minimum</Label>
                        <Input
                          id="participant-min"
                          type="number"
                          placeholder="1"
                          value={localCriteria.participantRange?.min || ''}
                          onChange={(e) => updateCriteria({
                            participantRange: {
                              ...localCriteria.participantRange,
                              min: e.target.value ? Number(e.target.value) : undefined
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="participant-max" className="text-xs">Maximum</Label>
                        <Input
                          id="participant-max"
                          type="number"
                          placeholder="1000"
                          value={localCriteria.participantRange?.max || ''}
                          onChange={(e) => updateCriteria({
                            participantRange: {
                              ...localCriteria.participantRange,
                              max: e.target.value ? Number(e.target.value) : undefined
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="p-4 border-t bg-muted/30">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={applyFilters} size="sm" className="flex-1">
                    Apply Filters
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {criteria.search && (
            <Badge variant="secondary" className="text-xs">
              Search: "{criteria.search}"
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('search')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {criteria.status?.map((status) => (
            <Badge key={status} variant="secondary" className="text-xs">
              {statusOptions.find(s => s.value === status)?.label}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('status', status)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {criteria.priorityLevels?.map((priority) => (
            <Badge key={priority} variant="secondary" className="text-xs">
              {priorityOptions.find(p => p.value === priority)?.label} Priority
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('priorityLevels', priority)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {criteria.requiresAttention && (
            <Badge variant="secondary" className="text-xs">
              Requires Attention
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('requiresAttention')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {(criteria.dateRange?.start || criteria.dateRange?.end) && (
            <Badge variant="secondary" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              Date Range
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeFilter('dateRange')}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}